import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Avatar,
  LinearProgress,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Drawer,
} from '@mui/material';
import {
  Search,
  TrendingUp,
  CheckCircle,
  Cancel,
  AttachMoney,
  Refresh,
  Visibility,
  Close,
  Person,
  CalendarToday,
  Inventory,
  Store,
  FitnessCenter,
  Timer,
  Download,
  AcUnit,
  Edit,
  PersonAdd,
  History,
  DeleteOutline,
  Add,
  Email,
  Phone,
  ArrowForward,
  EventRepeat,
  Payments,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ManualPackAssignDialog from '../components/ManualPackAssignDialog';
import ManualBookingDialog from '../components/ManualBookingDialog';
import BulkPackBookingDialog from '../components/BulkPackBookingDialog';
import { apiService } from '../services/apiService';
import { bookingStudioOf } from '../utils/studioOf';
import { Transaction, sourceLabel, effectiveTransactionSource, getPaymentModeColor } from './TransactionsPage';
import { colors } from '../theme/theme';
import { useAuth } from '../contexts/AuthContext';
import { isExpiredBeforeToday } from '../utils/packExpiry';
import { isOwnerRole } from '../utils/roles';
import { useMyLocations } from '../hooks/useMyLocations';
import BranchChip from '../components/BranchChip';

interface PurchasedPack {
  _id: string;
  user: {
    _id: string;
    name: string;
    email: string;
    phone?: string;
  };
  pack: {
    _id: string;
    title: string;
    description?: string;
    totalClasses?: number;
    totalPrice?: number;
    durationMonths?: number;
    durationWeeks?: number;
    durationDays?: number;
    image?: string;
  };
  studio?: {
    _id: string;
    studioName: string;
    email?: string;
  };
  title: string;
  image?: string;
  totalClasses?: number;
  classesLeft?: number;
  // Combo pack: per-category/class remaining quotas (populated names).
  categoryAllowances?: Array<{
    appliesTo?: string;
    category?: { _id?: string; title?: string } | string | null;
    class?: { _id?: string; className?: string } | string | null;
    total?: number | null;
    remaining?: number | null;
  }>;
  // Shared pack: classesLeft usable across these categories / classes (populated).
  allowedCategories?: Array<{ _id?: string; title?: string } | string>;
  allowedClasses?: Array<{ _id?: string; className?: string } | string>;
  pendingBookingsCount?: number;
  /**
   * Expiring view only (status=expiring): the member's other pack at this
   * studio that takes over from this one — they've already renewed. null =
   * not renewed. Absent on every other view.
   */
  renewedWith?: {
    _id: string;
    title?: string | null;
    purchaseDate?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    classesLeft?: number | null;
    totalClasses?: number | null;
  } | null;
  /** Bookings that drew on this pack and were not cancelled (past or future). Server-computed. */
  usedBookingsCount?: number;
  totalPrice?: number;
  purchaseDate: string;
  expiryDate?: string;
  packEndDate?: string;
  createdAt?: string;
  startDate?: string;
  isFrozen?: boolean;
  /** Same field as Booking.isPaid. Only `false` (manual assign left unticked) means owed. */
  isPaid?: boolean;
  /** Purchase transaction's basePrice + taxes (server-computed). */
  amountPaid?: number;
  frozenSince?: string;
  scheduledUnfreezeDate?: string;
  freezeHistory?: {
    _id?: string;
    startDate: string;
    endDate: string | null;
    daysUsed: number;
    month: string;
  }[];
  overrideRemainingFreezeDays?: number | null;
  // Pack's own lifetime freeze allowance (copied from the pack template); absent = studio policy.
  freezeDaysTotal?: number | null;
}

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

/**
 * The IST calendar day of a timestamp, as YYYY-MM-DD.
 *
 * Not `toISOString().split('T')[0]`, which is the UTC day: a start stored as
 * IST midnight (18:30Z the previous day) would show one day early.
 */
const istYmd = (d: string | Date): string =>
  new Date(new Date(d).getTime() + IST_OFFSET_MS).toISOString().slice(0, 10);

/**
 * The expiry day a studio pack gets when it starts on `startYmd`.
 *
 * Mirrors the backend's calculateExpiryDate: start + durationMonths +
 * duration days (durationDays, else durationWeeks × 7), valid through the end
 * of that IST day. Preview only — the server recalculates on save. Returns ''
 * when the pack has no duration to go on (e.g. a global pack).
 */
const expiryYmdFor = (startYmd: string, pack?: PurchasedPack['pack']): string => {
  if (!startYmd || !pack) return '';
  const months = pack.durationMonths ?? 0;
  const days =
    pack.durationDays != null ? pack.durationDays : (pack.durationWeeks ?? 0) * 7;
  if (!months && !days) return '';
  const [y, m, d] = startYmd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCMonth(dt.getUTCMonth() + months);
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
};

interface Stats {
  totalPurchases: number;
  globalPackPurchases: number;
  studioPackPurchases: number;
  activePacks: number;
  expiredPacks: number;
  totalRevenue: number;
}

interface Studio {
  _id: string;
  studioName: string;
}

const STATUS_FILTERS = ['all', 'active', 'expiring', 'expired', 'frozen'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

const PurchasedPacksPage: React.FC = () => {
  // Branch identity for each row while "All locations" aggregates them, and
  // the location to pin row writes to. See useMyLocations / apiService.atStudio.
  const { byId: locationById, isAllLocations } = useMyLocations();
  const studioOf = (pp: any): string | undefined => {
    const v = pp?.studio;
    return v ? String(v?._id ?? v) : undefined;
  };

  const navigate = useNavigate();
  const { admin } = useAuth();
  const isStudio = isOwnerRole(admin?.role) || admin?.role === 'manager';
  const isInstructor = admin?.role === 'instructor';
  // Pack whose "Bulk book classes" dialog is open.
  const [bulkBookPack, setBulkBookPack] = useState<PurchasedPack | null>(null);
  // Bulk booking is switched on per studio by a super admin. A studio login
  // reads its own studio (refreshed on every load); the admin list carries
  // the flag on each row's studio.
  const packStudioId = (pp: any): string => String(pp?.studio?._id ?? pp?.studio ?? '');
  const bulkBookingOn = (pp: any): boolean =>
    pp?.studio?.studioPermissions?.enableBulkPackBooking === true ||
    (!!packStudioId(pp) &&
      packStudioId(pp) === String((admin?.studio as any)?._id ?? '') &&
      (admin?.studio as any)?.studioPermissions?.enableBulkPackBooking === true);
  const isSuperAdmin = admin?.role === 'super_admin' || admin?.role === 'admin';
  const canDeletePurchasedPack = !isInstructor; // super_admin, studio_owner, manager all can delete
  const [purchasedPacks, setPurchasedPacks] = useState<PurchasedPack[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [packTypeFilter, setPackTypeFilter] = useState<'all' | 'global' | 'studio'>('all');
  // ?status=… preselects the filter — the dashboard's Expiring Soon popup
  // links here with status=expiring.
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() => {
    const s = searchParams.get('status');
    return STATUS_FILTERS.includes(s as StatusFilter) ? (s as StatusFilter) : 'all';
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPack, setSelectedPack] = useState<PurchasedPack | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [userPanelOpen, setUserPanelOpen] = useState(false);
  const [packTransaction, setPackTransaction] = useState<Transaction | null>(null);
  const [packTransactionLoading, setPackTransactionLoading] = useState(false);
  const [viewingPackReceipt, setViewingPackReceipt] = useState(false);
  const [studios, setStudios] = useState<Studio[]>([]);
  const [selectedStudio, setSelectedStudio] = useState<Studio | null>(
    isStudio && admin?.studio ? { _id: admin.studio._id, studioName: admin.studio.studioName } : null
  );
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // 50 per page when browsing. A search returns every match on one page —
  // "pankaj" should list all of Pankaj's packs, not the first 50.
  const PAGE_SIZE = 50;
  const SEARCH_PAGE_SIZE = 500;
  const isSearching = debouncedSearchTerm.trim().length > 0;
  const pageSize = isSearching ? SEARCH_PAGE_SIZE : PAGE_SIZE;

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editPack, setEditPack] = useState<PurchasedPack | null>(null);
  const [editClassesLeft, setEditClassesLeft] = useState<string>('');
  // Combo pack: one field per category / class (keyed like the server's
  // allowanceKey). Only one bucket may change per save.
  const [editBuckets, setEditBuckets] = useState<Record<string, string>>({});
  const [editExpiryDate, setEditExpiryDate] = useState<string>('');
  const [editStartDate, setEditStartDate] = useState<string>('');
  // True once the expiry field is edited by hand. Until then it follows the
  // start date, and is left for the server to recalculate on save.
  const [editExpiryTouched, setEditExpiryTouched] = useState(false);
  const [editFreezeDaysOverride, setEditFreezeDaysOverride] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Assign pack dialog state
  const [assignPackOpen, setAssignPackOpen] = useState(false);

  // History dialog state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyPack, setHistoryPack] = useState<PurchasedPack | null>(null);
  const [historyItems, setHistoryItems] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // Create booking from history dialog
  const [manualBookingOpen, setManualBookingOpen] = useState(false);

  // Booking status update from history dialog
  const [historyStatusDialogOpen, setHistoryStatusDialogOpen] = useState(false);
  const [historyBookingToUpdate, setHistoryBookingToUpdate] = useState<{ _id: string; bookingId: string; bookingStatus: string; userName: string } | null>(null);
  const [historyNewStatus, setHistoryNewStatus] = useState('');
  const [historyStatusReason, setHistoryStatusReason] = useState('');
  const [historyCreditClassBack, setHistoryCreditClassBack] = useState(true);
  const [isUpdatingHistoryStatus, setIsUpdatingHistoryStatus] = useState(false);

  // Admin freeze/unfreeze state
  const [freezeDialogOpen, setFreezeDialogOpen] = useState(false);
  const [freezeTargetPack, setFreezeTargetPack] = useState<PurchasedPack | null>(null);
  const [freezeStartDate, setFreezeStartDate] = useState('');
  const [freezeEndDate, setFreezeEndDate] = useState('');
  const [freezeReason, setFreezeReason] = useState('');
  const [isFreezing, setIsFreezing] = useState(false);
  const [freezeError, setFreezeError] = useState<string | null>(null);
  const [isUnfreezing, setIsUnfreezing] = useState<string | null>(null); // packId being unfrozen

  // Delete purchased pack state
  const [deletePackDialogOpen, setDeletePackDialogOpen] = useState(false);
  const [packToDelete, setPackToDelete] = useState<PurchasedPack | null>(null);
  const [isDeletingPack, setIsDeletingPack] = useState(false);
  const [deletePackError, setDeletePackError] = useState<string | null>(null);

  // Delete freeze history entry state (super admin only)
  const [deleteFreezeEntryDialogOpen, setDeleteFreezeEntryDialogOpen] = useState(false);
  const [freezeEntryToDelete, setFreezeEntryToDelete] = useState<{ packId: string; entryId: string; month: string; studioId?: string } | null>(null);
  const [isDeletingFreezeEntry, setIsDeletingFreezeEntry] = useState(false);
  const [deleteFreezeEntryError, setDeleteFreezeEntryError] = useState<string | null>(null);

  const fetchStudios = useCallback(async () => {
    if (isStudio) return; // Studio role doesn't need to fetch all studios
    try {
      const response = await apiService.getStudios();
      setStudios(response?.data || response?.studios || []);
    } catch (err) {
      console.error('Error fetching studios:', err);
    }
  }, [isStudio]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiService.getPurchasedPacksStats();
      setStats(response);
    } catch (err: any) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  const fetchPurchasedPacks = useCallback(async (signal?: AbortSignal) => {
    try {
      setIsLoading(true);
      setError(null);
      const params: any = {
        page: isSearching ? 1 : page,
        pageSize,
      };

      if (isSearching) params.search = debouncedSearchTerm.trim();
      if (packTypeFilter !== 'all') params.packType = packTypeFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (selectedStudio) params.studioId = selectedStudio._id;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await apiService.getPurchasedPacks({ ...params, signal });
      setPurchasedPacks(response.purchasedPacks || []);
      setTotalPages(response.pages || 1);
      setTotalCount(response.count || 0);
    } catch (err: any) {
      if (axios.isCancel(err) || err.code === 'ERR_CANCELED') return;
      console.error('Error fetching purchased packs:', err);
      setError(err.message || 'Failed to fetch purchased packs');
      setPurchasedPacks([]);
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }, [page, pageSize, isSearching, packTypeFilter, statusFilter, selectedStudio, startDate, endDate, debouncedSearchTerm]);

  useEffect(() => {
    fetchStudios();
    fetchStats();
  }, [fetchStudios, fetchStats]);

  // Only reissue the request once the search box has settled, and cancel any
  // still-in-flight request before starting the next one — otherwise a slow
  // response for an earlier keystroke can resolve after a newer one and
  // clobber it with stale (and usually longer) results.
  useEffect(() => {
    const controller = new AbortController();
    fetchPurchasedPacks(controller.signal);
    return () => controller.abort();
  }, [fetchPurchasedPacks]);

  // Short settle time so search feels immediate; the in-flight request is
  // aborted on every new keystroke (effect above), so a slow earlier response
  // can never overwrite a newer one.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleRefresh = () => {
    fetchStats();
    fetchPurchasedPacks();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // "Pilates · Mat · Yoga" for a shared pack, else null.
  const sharedScopeOf = (pack: PurchasedPack): string | null => {
    const list: any[] = pack.allowedCategories?.length ? pack.allowedCategories : pack.allowedClasses ?? [];
    if (!list.length) return null;
    const names = list.map((x: any) => (typeof x === 'object' ? x?.title ?? x?.className : null)).filter(Boolean);
    return names.length ? names.join(' · ') : 'the selected categories';
  };

  // Mirrors PP app: classesLeft + pending BOOKED bookings — sessions are only
  // "consumed" once CHECKED_IN or NO_SHOW, same as the mobile app display.
  const effectiveClassesLeft = (pack: PurchasedPack) =>
    (pack.classesLeft ?? 0) + (pack.pendingBookingsCount ?? 0);

  const isPackActive = (pack: PurchasedPack) => {
    const now = new Date();
    if (isExpiredBeforeToday(pack.expiryDate, now)) return false;
    if (isExpiredBeforeToday(pack.packEndDate, now)) return false;
    // Only check classesLeft for limited packs (unlimited packs have no totalClasses)
    if (pack.totalClasses && effectiveClassesLeft(pack) <= 0) return false;
    return true;
  };

  const getDaysRemaining = (pack: PurchasedPack) => {
    const endDate = pack.expiryDate || pack.packEndDate;
    if (!endDate) return null;
    const now = new Date();
    const end = new Date(endDate);
    const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Bar / count colour — same rule as the partner app's
  // PackClassesLeftIndicator and the Expiring Soon filter:
  //   red    used up: no credits left and nothing booked ahead
  //   orange 2 or fewer credits left (Expiring Soon)
  //   green  otherwise
  const remainingColor = (pack: PurchasedPack) => {
    const credits = pack.classesLeft ?? 0;
    if (credits <= 0 && (pack.pendingBookingsCount ?? 0) === 0) return '#F44336';
    if (credits <= 2) return '#FF9800';
    return '#4CAF50';
  };

  const getClassUsagePercent = (pack: PurchasedPack) => {
    if (!pack.totalClasses || pack.totalClasses === 0) return null;
    const used = pack.totalClasses - effectiveClassesLeft(pack);
    return Math.round((used / pack.totalClasses) * 100);
  };

  const handleViewDetails = (pack: PurchasedPack) => {
    setSelectedPack(pack);
    setDetailDialogOpen(true);
  };

  const handleUserClick = (userId: string) => {
    navigate(`/users/${userId}`);
  };

  // Opens the right-side user/subscription panel for a given row without
  // leaving the Pack Subscriptions page.
  const handleOpenUserPanel = (pack: PurchasedPack) => {
    setSelectedPack(pack);
    setUserPanelOpen(true);
  };

  // The purchased pack itself carries no link to its payment — the relationship
  // only exists the other way (Transaction.purchasedPack) — so the panel looks
  // it up from that user's transaction history and matches on the pack id,
  // the same list UserDetailPage's Transaction History tab already fetches.
  useEffect(() => {
    if (!userPanelOpen || !selectedPack?.user?._id) {
      setPackTransaction(null);
      return;
    }
    let cancelled = false;
    setPackTransactionLoading(true);
    setPackTransaction(null);
    // Scoped tightly around the pack's own purchase date (rather than just
    // paging through the user's whole transaction history) — a user with
    // more than a page's worth of transactions could otherwise have this
    // pack's own transaction fall off page 1 and wrongly read as "no record".
    const purchaseMs = new Date(selectedPack.purchaseDate).getTime();
    const startDate = new Date(purchaseMs - 2 * 86400000).toISOString();
    const endDate = new Date(purchaseMs + 2 * 86400000).toISOString();
    apiService
      .getUserTransactionHistory(selectedPack.user._id, { pageSize: 100, sort: '-1', startDate, endDate })
      .then((response: any) => {
        if (cancelled) return;
        // A pack can have MORE than one transaction referencing it: the real
        // purchase payment, plus one zero-value "subscription" mode entry per
        // class check-in that consumed the pack's own credit (booking set,
        // basePrice 0 — see packs.service.ts's manual walk-in flow). Only the
        // former has no `booking` — exclude usage entries so this never picks
        // up "subscription" as if the pack had somehow paid for itself.
        const match = (response.transactions || []).find(
          (t: Transaction) => t.purchasedPack?._id === selectedPack._id && !t.booking
        );
        setPackTransaction(match || null);
      })
      .catch(() => {
        if (!cancelled) setPackTransaction(null);
      })
      .finally(() => {
        if (!cancelled) setPackTransactionLoading(false);
      });
    return () => { cancelled = true; };
  }, [userPanelOpen, selectedPack?.user?._id, selectedPack?._id, selectedPack?.purchaseDate]);

  // Same view-receipt behaviour as the Transactions page: fetch the PDF as a
  // blob and open it in a new tab, rather than a new/duplicate implementation.
  const handleViewPackReceipt = async () => {
    if (!packTransaction) return;
    try {
      setViewingPackReceipt(true);
      const blob = await apiService.downloadTransactionReceipt(packTransaction._id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err: any) {
      setError(err.message || 'Failed to view receipt');
    } finally {
      setViewingPackReceipt(false);
    }
  };

  // One icon + label/value field in the user panel's Pack Information card —
  // deliberately monochrome (icon tinted with the brand colour) rather than a
  // different accent per field, to keep the panel looking calm and cohesive.
  const renderInfoField = (Icon: React.ElementType, label: string, value: React.ReactNode, wrap = false) => (
    <Box display="flex" alignItems="flex-start" gap={1}>
      <Icon sx={{ fontSize: 17, color: colors.primaryDark, mt: '2px' }} />
      <Box minWidth={0}>
        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
        <Typography variant="body2" fontWeight={600} noWrap={!wrap}>{value}</Typography>
      </Box>
    </Box>
  );

  // A clearly-weighted section heading for the user panel — bold and dark
  // rather than the small grey subtitle used inside the wider dialogs, since
  // in the narrower panel that reads as too quiet to anchor a section.
  const renderPanelSectionHeading = (label: string) => (
    <Typography
      variant="subtitle1"
      sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5 }}
    >
      {label}
    </Typography>
  );

  // Shared by the "Subscription Details" dialog and the user side panel so
  // both surfaces stay in sync — this is the same freeze history block, just
  // parameterized on whichever pack is being shown.
  const renderFreezeSection = (pack: PurchasedPack, variant: 'table' | 'log' = 'table') => {
    if (!pack.studio) return null;
    const history = pack.freezeHistory ?? [];
    const packAllowance = Number(pack.freezeDaysTotal) > 0 ? Number(pack.freezeDaysTotal) : null;
    if (!pack.isFrozen && history.length === 0 && packAllowance == null) return null;
    const allowanceUsed = history
      .filter(h => h.endDate !== null)
      .reduce((sum, h) => sum + (h.daysUsed ?? 0), 0);
    const allowanceLine = packAllowance != null ? (
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Pack freeze allowance:</strong> {packAllowance} days for the life of the pack
        {' · '}{allowanceUsed} used · {Math.max(0, packAllowance - allowanceUsed)} left
      </Typography>
    ) : null;

    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const completedThisMonth = history.filter(h => h.month === currentMonth && h.endDate !== null);
    const daysUsedThisMonth = completedThisMonth.reduce((sum, h) => sum + (h.daysUsed ?? 0), 0);
    const currentFrozenDays = pack.isFrozen && pack.frozenSince
      ? Math.max(0, Math.floor((now.getTime() - new Date(pack.frozenSince).getTime()) / 86400000))
      : 0;
    // Only worth a combined "days used this month" line when more than one
    // freeze period contributes to it — otherwise it just repeats the single
    // number already visible in "Days So Far" or in that one entry's row.
    const monthlyContributors = completedThisMonth.length + (pack.isFrozen && pack.frozenSince ? 1 : 0);
    const showMonthlyTotal = monthlyContributors > 1;

    const deleteEntryButton = (entry: NonNullable<PurchasedPack['freezeHistory']>[number]) =>
      isSuperAdmin && entry._id ? (
        <Tooltip title="Delete entry (super admin)">
          <IconButton
            size="small"
            color="error"
            onClick={() => {
              setFreezeEntryToDelete({ packId: pack._id, entryId: entry._id!, month: entry.month, studioId: studioOf(pack) });
              setDeleteFreezeEntryError(null);
              setDeleteFreezeEntryDialogOpen(true);
            }}
          >
            <DeleteOutline sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : null;

    // 'log' variant: everything — current-freeze summary, this month's usage,
    // and past entries — collected into ONE bordered card (brand-tinted where
    // it's actively frozen) instead of several loose, differently-styled
    // pieces, so it reads as a single "Freeze History" unit.
    if (variant === 'log') {
      return (
        <Box sx={{ border: `1px solid ${colors.divider}`, borderRadius: 3, overflow: 'hidden' }}>
          {allowanceLine && <Box sx={{ px: 2, pt: 1.5 }}>{allowanceLine}</Box>}
          {pack.isFrozen && pack.frozenSince && (
            <Box sx={{ p: 2, backgroundColor: `${colors.primary}12` }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AcUnit sx={{ fontSize: 16, color: colors.primaryDark }} />
                <Typography variant="body2" fontWeight={700} sx={{ color: colors.primaryDark }}>
                  Currently Frozen
                </Typography>
              </Box>
              <Box display="flex" flexWrap="wrap" gap={2.5}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Frozen Since</Typography>
                  <Typography variant="body2" fontWeight={600}>{formatDateTime(pack.frozenSince)}</Typography>
                </Box>
                {pack.scheduledUnfreezeDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Resumes On</Typography>
                    <Typography variant="body2" fontWeight={600}>{formatDate(pack.scheduledUnfreezeDate)}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">Days So Far</Typography>
                  <Typography variant="body2" fontWeight={600}>{currentFrozenDays} day{currentFrozenDays !== 1 ? 's' : ''}</Typography>
                </Box>
                {pack.expiryDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Expiry Extends To</Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {formatDate(new Date(new Date(pack.expiryDate).getTime() + currentFrozenDays * 86400000).toISOString())}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Combined monthly total — only when it adds information beyond a
              single number already visible above or in one entry row below
              (i.e. more than one freeze period landed in the current month). */}
          {showMonthlyTotal && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 2,
                py: 1.5,
                borderTop: pack.isFrozen && pack.frozenSince ? `1px solid ${colors.divider}` : 'none',
              }}
            >
              <Typography variant="body2" color="text.secondary">Days used this month</Typography>
              <Typography variant="body2" fontWeight={700}>{daysUsedThisMonth + currentFrozenDays} days</Typography>
            </Box>
          )}

          {history.length > 0 && (
            <Box
              sx={{
                borderTop: (pack.isFrozen && pack.frozenSince) || showMonthlyTotal ? `1px solid ${colors.divider}` : 'none',
                px: 2,
              }}
            >
              {[...history].reverse().map((entry, i, arr) => (
                <React.Fragment key={entry._id ?? i}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
                    <AcUnit sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{entry.month}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {formatDate(entry.startDate)} – {entry.endDate ? formatDate(entry.endDate) : 'ongoing'}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                      {entry.endDate ? (
                        <>
                          <Typography variant="body2" fontWeight={700}>
                            {entry.daysUsed} day{entry.daysUsed !== 1 ? 's' : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            +{entry.daysUsed}d extended
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="caption" fontWeight={600} color="text.secondary">Ongoing</Typography>
                      )}
                    </Box>
                    {deleteEntryButton(entry)}
                  </Box>
                  {i < arr.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Box>
          )}
        </Box>
      );
    }

    // 'table' variant — unchanged, used by the wider "Subscription Details" dialog.
    return (
      <>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          FREEZE INFORMATION
        </Typography>
        {allowanceLine}

        {pack.isFrozen && pack.frozenSince && (
          <Box
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: 2,
              border: `1px solid ${colors.divider}`,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <AcUnit sx={{ fontSize: 16, color: '#455A64' }} />
              <Typography variant="body2" fontWeight={600} color="#455A64">
                Currently Frozen
              </Typography>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={2} mt={1}>
              <Box>
                <Typography variant="caption" color="text.secondary">Frozen Since</Typography>
                <Typography variant="body2" fontWeight={500}>{formatDateTime(pack.frozenSince)}</Typography>
              </Box>
              {pack.scheduledUnfreezeDate && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Scheduled Unfreeze</Typography>
                  <Typography variant="body2" fontWeight={500}>{formatDate(pack.scheduledUnfreezeDate)}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">Days Frozen So Far</Typography>
                <Typography variant="body2" fontWeight={500}>{currentFrozenDays} day{currentFrozenDays !== 1 ? 's' : ''}</Typography>
              </Box>
              {pack.expiryDate && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Expiry Will Be Extended To</Typography>
                  <Typography variant="body2" fontWeight={600} color="success.main">
                    {formatDate(new Date(new Date(pack.expiryDate).getTime() + currentFrozenDays * 86400000).toISOString())}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Box display="flex" gap={3} mb={2} flexWrap="wrap">
          <Box>
            <Typography variant="caption" color="text.secondary">Days Used This Month</Typography>
            <Typography variant="body2" fontWeight={600}>{daysUsedThisMonth + currentFrozenDays}</Typography>
          </Box>
        </Box>

        {history.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              FREEZE HISTORY
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Month</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Frozen From</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Unfrozen On</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Days Used</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Expiry Extended By</TableCell>
                    {isSuperAdmin && <TableCell sx={{ fontWeight: 600, fontSize: 12 }} />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...history].reverse().map((entry, i) => (
                    <TableRow key={entry._id ?? i} sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ fontSize: 12 }}>{entry.month}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{formatDate(entry.startDate)}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {entry.endDate ? (
                          formatDate(entry.endDate)
                        ) : (
                          <Chip label="Ongoing" size="small" sx={{ backgroundColor: '#1A1A1A15', color: '#455A64', fontWeight: 600, fontSize: 11 }} icon={<AcUnit sx={{ fontSize: '12px !important' }} />} />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{entry.endDate ? `${entry.daysUsed} day${entry.daysUsed !== 1 ? 's' : ''}` : `${currentFrozenDays}d so far`}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {entry.endDate ? (
                          <Typography variant="caption" color="success.main" fontWeight={600}>
                            +{entry.daysUsed} day{entry.daysUsed !== 1 ? 's' : ''}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">Pending</Typography>
                        )}
                      </TableCell>
                      {isSuperAdmin && (
                        <TableCell sx={{ fontSize: 12, p: 0.5 }}>{deleteEntryButton(entry)}</TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </>
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setPackTypeFilter('all');
    setStatusFilter('all');
    setSelectedStudio(null);
    setStartDate('');
    setEndDate('');
    setPage(1);
    // Drop a ?status= the page was opened with, so a refresh doesn't bring it back.
    if (searchParams.has('status')) setSearchParams({}, { replace: true });
  };

  /**
   * Freeze dates use the RESUME-DAY contract, matching the member's app: the
   * second date is the day the pack comes back, so it is always at least one day
   * after the pause begins and the pause length is simply the difference.
   */
  const nextDayOf = (isoDay: string) => {
    if (!isoDay) return new Date().toISOString().split('T')[0];
    const d = new Date(`${isoDay}T00:00:00.000Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().split('T')[0];
  };
  const frozenDayCount = (startDay: string, resumeDay: string) => {
    if (!startDay || !resumeDay) return 0;
    const ms = Date.parse(`${resumeDay}T00:00:00.000Z`) - Date.parse(`${startDay}T00:00:00.000Z`);
    return Math.max(0, Math.round(ms / 86400000));
  };

  // Returns true only if the pack is expired due to date (not just classes exhausted)
  const isDateExpired = (pack: PurchasedPack) => {
    const now = new Date();
    if (isExpiredBeforeToday(pack.expiryDate, now)) return true;
    if (isExpiredBeforeToday(pack.packEndDate, now)) return true;
    return false;
  };

  // Returns true only if classes ran out (and date is still valid or not set)
  const isClassesExhausted = (pack: PurchasedPack) => {
    if (isDateExpired(pack)) return false;
    return !!(pack.totalClasses && effectiveClassesLeft(pack) <= 0);
  };

  // A pack is "upcoming" when its startDate is explicitly set to a future date.
  // Used for DISPLAY (the "Upcoming" chip / "Starts…Expires" layout) — keep it
  // strict so a today-purchased active pack is not shown as upcoming.
  const isUpcomingPack = (pack: PurchasedPack) => {
    if (!pack.startDate) return false;
    return new Date(pack.startDate) > new Date();
  };

  // True when at least one session has been booked against this pack: either an
  // upcoming BOOKED session (pendingBookingsCount) or a class already consumed
  // on a limited pack (classesLeft < totalClasses). Once a session exists,
  // moving the start date could orphan it, so we lock the start date.
  //
  // `usedBookingsCount` is the server's answer (every non-cancelled booking on
  // the pack, past or future). The older heuristic is only the fallback for a
  // backend that does not send it yet.
  const hasBookedSession = (pack: PurchasedPack) =>
    pack.usedBookingsCount !== undefined
      ? pack.usedBookingsCount > 0
      : (pack.pendingBookingsCount ?? 0) > 0 ||
        (!!pack.totalClasses && (pack.classesLeft ?? 0) < pack.totalClasses);

  // Only super admins and studio owners manage a pack's start date.
  const canManageStartDate = isSuperAdmin || isOwnerRole(admin?.role);

  // The start date stays editable until the member books with the pack —
  // whether or not it has started yet. The server enforces the same rule.
  const canEditStartDate = (pack: PurchasedPack) =>
    canManageStartDate && !hasBookedSession(pack);

  /** The server's allowanceKey for a combo bucket. */
  const bucketKeyOf = (b: NonNullable<PurchasedPack['categoryAllowances']>[number]) => {
    const ref = (v: any) => (v == null ? '*' : typeof v === 'object' ? String(v._id ?? '*') : String(v));
    return b.appliesTo === 'class'
      ? `class:${ref(b.class)}`
      : `${b.appliesTo === 'wellness' ? 'wellness' : 'session'}:${ref(b.category)}`;
  };
  const bucketNameOf = (b: NonNullable<PurchasedPack['categoryAllowances']>[number]) => {
    if (b.appliesTo === 'class') return (typeof b.class === 'object' ? b.class?.className : undefined) ?? 'Class';
    const title = typeof b.category === 'object' ? b.category?.title : undefined;
    if (b.appliesTo === 'wellness') return title ?? 'Any wellness';
    return title ?? 'Any workout';
  };
  const isComboPack = (pack?: PurchasedPack | null) => (pack?.categoryAllowances?.length ?? 0) > 0;

  const handleOpenEdit = (pack: PurchasedPack, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditPack(pack);
    setEditClassesLeft(pack.classesLeft !== undefined ? String(pack.classesLeft) : '');
    setEditBuckets(
      Object.fromEntries(
        (pack.categoryAllowances ?? [])
          .filter((b) => b.remaining != null)
          .map((b) => [bucketKeyOf(b), String(b.remaining)]),
      ),
    );
    const expiry = pack.expiryDate || pack.packEndDate;
    setEditExpiryDate(expiry ? istYmd(expiry) : '');
    setEditExpiryTouched(false);
    // A pack with no explicit startDate started on its purchase day.
    const start = pack.startDate || pack.purchaseDate;
    setEditStartDate(start ? istYmd(start) : '');
    setEditFreezeDaysOverride(pack.overrideRemainingFreezeDays != null
      ? String(pack.overrideRemainingFreezeDays)
      : '');
    setEditError(null);
    setEditDialogOpen(true);
  };

  const handleOpenHistory = async (pack: PurchasedPack, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setHistoryPack(pack);
    setHistoryItems([]);
    setHistoryError(null);
    setHistoryDialogOpen(true);
    setHistoryLoading(true);
    try {
      const response = await apiService.getPurchasedPackHistory(pack._id);
      setHistoryItems(response.history || []);
    } catch (err: any) {
      setHistoryError(err?.response?.data?.message || err.message || 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const refreshHistory = async () => {
    if (!historyPack) return;
    setHistoryLoading(true);
    try {
      const response = await apiService.getPurchasedPackHistory(historyPack._id);
      setHistoryItems(response.history || []);
    } catch (err: any) {
      setHistoryError(err?.response?.data?.message || err.message || 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDeletePackClick = (pack: PurchasedPack, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setPackToDelete(pack);
    setDeletePackError(null);
    setDeletePackDialogOpen(true);
  };

  const handleDeletePackConfirm = async () => {
    if (!packToDelete) return;
    setIsDeletingPack(true);
    setDeletePackError(null);
    try {
      await apiService.deletePurchasedPack(packToDelete._id, studioOf(packToDelete));
      setDeletePackDialogOpen(false);
      setPackToDelete(null);
      fetchPurchasedPacks();
    } catch (err: any) {
      setDeletePackError(err?.response?.data?.message || err.message || 'Failed to delete pack');
    } finally {
      setIsDeletingPack(false);
    }
  };

  const handleHistoryStatusUpdateConfirm = async () => {
    if (!historyBookingToUpdate || !historyNewStatus) return;
    setIsUpdatingHistoryStatus(true);
    try {
      const goingToCancel = historyNewStatus === 'CANCELLED' && historyBookingToUpdate.bookingStatus !== 'CANCELLED';
      const goingFromCancel = historyBookingToUpdate.bookingStatus === 'CANCELLED' && (historyNewStatus === 'BOOKED' || historyNewStatus === 'CHECKED_IN');
      const sendCreditClassBack = (goingToCancel || goingFromCancel) ? historyCreditClassBack : undefined;
      // Pinned — under "All locations" the switcher sends __all__, which the
      // server refuses for writes. The booking's own studio when the history
      // row carries one, else the pack's.
      await apiService.updateBookingStatus(
        historyBookingToUpdate._id,
        historyNewStatus,
        historyStatusReason || undefined,
        sendCreditClassBack,
        bookingStudioOf(historyBookingToUpdate) || studioOf(historyPack),
      );

      // Update booking status chip in history list
      setHistoryItems(prev =>
        prev.map(item =>
          item.bookingMongoId?.toString() === historyBookingToUpdate._id
            ? { ...item, bookingStatus: historyNewStatus }
            : item,
        ),
      );

      // Immediately reflect classesLeft change without a refresh
      const creditDelta = historyCreditClassBack
        ? goingToCancel ? 1 : goingFromCancel ? -1 : 0
        : 0;
      if (creditDelta !== 0 && historyPack) {
        const newClassesLeft = (historyPack.classesLeft ?? 0) + creditDelta;
        setHistoryPack(prev => prev ? { ...prev, classesLeft: newClassesLeft } : prev);
        setPurchasedPacks(prev =>
          prev.map(p => p._id === historyPack._id ? { ...p, classesLeft: newClassesLeft } : p),
        );
        if (selectedPack?._id === historyPack._id) {
          setSelectedPack(prev => prev ? { ...prev, classesLeft: newClassesLeft } : prev);
        }
      }

      setHistoryStatusDialogOpen(false);
      setHistoryBookingToUpdate(null);
    } catch (err: any) {
      setHistoryError(err?.response?.data?.message || err.message || 'Failed to update status');
    } finally {
      setIsUpdatingHistoryStatus(false);
    }
  };

  const handleOpenFreeze = (pack: PurchasedPack, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const today = new Date().toISOString().split('T')[0];
    setFreezeTargetPack(pack);
    setFreezeStartDate(today);
    setFreezeEndDate(today);
    setFreezeReason('');
    setFreezeError(null);
    setFreezeDialogOpen(true);
  };

  const handleConfirmFreeze = async () => {
    if (!freezeTargetPack) return;
    setIsFreezing(true);
    setFreezeError(null);
    try {
      await apiService.freezePackAdmin(freezeTargetPack._id, { startDate: freezeStartDate, endDate: freezeEndDate, reason: freezeReason || undefined }, studioOf(freezeTargetPack));
      setFreezeDialogOpen(false);
      fetchPurchasedPacks();
    } catch (err: any) {
      setFreezeError(err?.response?.data?.message || err.message || 'Failed to freeze pack');
    } finally {
      setIsFreezing(false);
    }
  };

  const handleUnfreeze = async (pack: PurchasedPack, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!window.confirm(`Unfreeze "${pack.title}" for ${pack.user?.name}?`)) return;
    setIsUnfreezing(pack._id);
    try {
      await apiService.unfreezePackAdmin(pack._id, studioOf(pack));
      fetchPurchasedPacks();
    } catch (err: any) {
      alert(err?.response?.data?.message || err.message || 'Failed to unfreeze pack');
    } finally {
      setIsUnfreezing(null);
    }
  };

  // Same flow as the Bookings page: the row's "Mark paid" button opens this
  // confirmation.
  const [markPaidTarget, setMarkPaidTarget] = useState<PurchasedPack | null>(null);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);

  const handleMarkPaidConfirm = async () => {
    const target = markPaidTarget;
    if (!target) return;
    setIsMarkingPaid(true);
    try {
      await apiService.updatePurchasedPack(target._id, { isPaid: true }, studioOf(target));
      setPurchasedPacks(prev => prev.map(p => (p._id === target._id ? { ...p, isPaid: true } : p)));
      setMarkPaidTarget(null);
    } catch (err: any) {
      alert(err?.response?.data?.message || err.message || 'Failed to mark as paid');
      setMarkPaidTarget(null);
    } finally {
      setIsMarkingPaid(false);
    }
  };

  const handleDeleteFreezeEntry = async () => {
    if (!freezeEntryToDelete) return;
    setIsDeletingFreezeEntry(true);
    setDeleteFreezeEntryError(null);
    try {
      await apiService.deleteFreezeHistoryEntry(freezeEntryToDelete.packId, freezeEntryToDelete.entryId, freezeEntryToDelete.studioId);
      // Update the selectedPack in state to remove the deleted entry
      setSelectedPack(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          freezeHistory: (prev.freezeHistory ?? []).filter(h => h._id !== freezeEntryToDelete.entryId),
        };
      });
      // Also update in the main list
      setPurchasedPacks(prev =>
        prev.map(p =>
          p._id === freezeEntryToDelete.packId
            ? { ...p, freezeHistory: (p.freezeHistory ?? []).filter(h => h._id !== freezeEntryToDelete.entryId) }
            : p,
        ),
      );
      setDeleteFreezeEntryDialogOpen(false);
      setFreezeEntryToDelete(null);
    } catch (err: any) {
      setDeleteFreezeEntryError(err?.response?.data?.message || err.message || 'Failed to delete entry');
    } finally {
      setIsDeletingFreezeEntry(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editPack) return;
    setIsSaving(true);
    setEditError(null);
    try {
      const payload: any = {};
      if (isComboPack(editPack)) {
        // Combo: send only the ONE bucket that changed, by its key.
        const changed = (editPack.categoryAllowances ?? []).filter(
          (b) => b.remaining != null && editBuckets[bucketKeyOf(b)] !== undefined &&
            editBuckets[bucketKeyOf(b)] !== '' && Number(editBuckets[bucketKeyOf(b)]) !== b.remaining,
        );
        if (changed.length > 1) {
          setEditError('Change one category or class at a time.');
          setIsSaving(false);
          return;
        }
        if (changed.length === 1) {
          const b = changed[0];
          const newVal = Number(editBuckets[bucketKeyOf(b)]);
          if (!Number.isInteger(newVal) || newVal < 0) {
            setEditError('Enter a whole number of classes.');
            setIsSaving(false);
            return;
          }
          if (newVal < (b.remaining ?? 0) - 1) {
            setEditError(`You can only reduce ${bucketNameOf(b)} by 1 at a time. Current: ${b.remaining}.`);
            setIsSaving(false);
            return;
          }
          payload.allowanceKey = bucketKeyOf(b);
          payload.allowanceRemaining = newVal;
        }
      } else if (editPack.totalClasses && editClassesLeft !== '') {
        const newVal = Number(editClassesLeft);
        const currentVal = editPack.classesLeft ?? 0;
        if (newVal < currentVal - 1) {
          setEditError(`You can only reduce classes by 1 at a time. Current: ${currentVal}, minimum allowed: ${currentVal - 1}.`);
          setIsSaving(false);
          return;
        }
        payload.classesLeft = newVal;
      }
      const existingStart = editPack.startDate || editPack.purchaseDate;
      const existingStartStr = existingStart ? istYmd(existingStart) : '';
      const startChanged =
        canEditStartDate(editPack) && !!editStartDate && editStartDate !== existingStartStr;
      if (startChanged) {
        payload.startDate = editStartDate;
      }

      // Expiry is sent only when the admin set it by hand and it differs from
      // what is stored. Echoing it back alongside a new start date would pin
      // the OLD expiry — the server treats a sent expiry as a deliberate choice
      // and skips recalculating it from the new start.
      const existingExpiry = editPack.expiryDate || editPack.packEndDate;
      const existingExpiryStr = existingExpiry ? istYmd(existingExpiry) : '';
      if (editExpiryDate && editExpiryTouched && editExpiryDate !== existingExpiryStr) {
        if (editPack.expiryDate) payload.expiryDate = editExpiryDate;
        else payload.packEndDate = editExpiryDate;
      }
      if (isSuperAdmin && editFreezeDaysOverride !== '') {
        payload.overrideRemainingFreezeDays = Number(editFreezeDaysOverride);
      }
      const response = await apiService.updatePurchasedPack(editPack._id, payload, studioOf(editPack));
      // Refresh the updated pack in the list
      setPurchasedPacks(prev =>
        prev.map(p => (p._id === editPack._id ? { ...p, ...response.purchasedPack } : p)),
      );
      // Also update the detail dialog if it's open for the same pack
      if (selectedPack?._id === editPack._id) {
        setSelectedPack({ ...selectedPack, ...response.purchasedPack });
      }
      setEditDialogOpen(false);
    } catch (err: any) {
      setEditError(err?.response?.data?.message || err.message || 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const StatCard = ({
    title,
    value,
    icon,
    color,
  }: {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
  }) => (
    <Card
      sx={{
        flex: '1 1 150px',
        minWidth: 150,
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}30`,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 20px ${color}20`,
        },
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase">
              {title}
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ color, mt: 0.5 }}>
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}20`,
              borderRadius: 2,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {React.cloneElement(icon as React.ReactElement<any>, { sx: { color, fontSize: 28 } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Pack Subscriptions
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Track and manage all pack purchases by users
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => setAssignPackOpen(true)}
            size="small"
          >
            Assign Pack
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Data">
            <IconButton color="primary">
              <Download />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
          <StatCard
            title="Total Purchases"
            value={stats.totalPurchases}
            icon={<TrendingUp />}
            color={colors.primary}
          />
          {!isStudio && (
            <StatCard
              title="Global Packs"
              value={stats.globalPackPurchases}
              icon={<Inventory />}
              color="#2196F3"
            />
          )}
          {!isStudio && (
            <StatCard
              title="Studio Packs"
              value={stats.studioPackPurchases}
              icon={<Store />}
              color="#9C27B0"
            />
          )}
          <StatCard
            title="Active"
            value={stats.activePacks}
            icon={<CheckCircle />}
            color="#4CAF50"
          />
          <StatCard
            title="Expired"
            value={stats.expiredPacks}
            icon={<Cancel />}
            color="#F44336"
          />
          {!isStudio && (
            <StatCard
              title="Revenue"
              value={formatCurrency(stats.totalRevenue)}
              icon={<AttachMoney />}
              color="#FF9800"
            />
          )}
        </Box>
      )}

      {/* Expiring view (usually opened from the dashboard's Expiring Soon
          popup) — say so, with a one-click way back to the full list. */}
      {statusFilter === 'expiring' && (
        <Alert
          severity="warning"
          sx={{ mb: 2, borderRadius: 2, alignItems: 'center' }}
          action={
            <Button color="inherit" size="small" onClick={clearFilters} sx={{ fontWeight: 600 }}>
              Clear
            </Button>
          }
        >
          Showing packs ending in the next 7 days or with 2 or fewer sessions left
          (used-up packs are left out). Members marked Renewed have already bought another pack.
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            <TextField
              placeholder="Search by member name, email or phone"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ minWidth: 250, flex: '1 1 250px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Pack Type</InputLabel>
              <Select
                value={packTypeFilter}
                label="Pack Type"
                onChange={(e) => {
                  setPackTypeFilter(e.target.value as any);
                  setPage(1);
                }}
              >
                <MenuItem value="all">All Types</MenuItem>
                {!isStudio && <MenuItem value="global">Global</MenuItem>}
                <MenuItem value="studio">Studio</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value as any);
                  setPage(1);
                  if (searchParams.has('status')) setSearchParams({}, { replace: true });
                }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="expiring">Expiring (7 days / 2 left)</MenuItem>
                <MenuItem value="expired">Expired / Exhausted</MenuItem>
                <MenuItem value="frozen">Frozen</MenuItem>
              </Select>
            </FormControl>
            {!isStudio && (
              <Autocomplete
                size="small"
                options={studios}
                getOptionLabel={(option) => option.studioName}
                value={selectedStudio}
                onChange={(_, newValue) => {
                  setSelectedStudio(newValue);
                  setPage(1);
                }}
                renderInput={(params) => <TextField {...params} label="Studio" placeholder="Select" />}
                sx={{ minWidth: 180 }}
              />
            )}
            <TextField
              type="date"
              label="From"
              size="small"
              sx={{ width: 140 }}
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="To"
              size="small"
              sx={{ width: 140 }}
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="outlined" size="small" onClick={clearFilters}>
              Clear
            </Button>
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
            <Typography variant="body2" color="text.secondary">
              {isSearching
                ? `${totalCount} subscription${totalCount === 1 ? '' : 's'} found for “${debouncedSearchTerm.trim()}”`
                : `Showing ${purchasedPacks.length} of ${totalCount} subscriptions`}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
              <CircularProgress />
            </Box>
          ) : purchasedPacks.length === 0 ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" py={6}>
              <Inventory sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary" variant="h6">
                No pack subscriptions found
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Try adjusting your filters or search term
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: colors.primary + '10' }}>
                  <TableCell sx={{ fontWeight: 600, width: 50 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Pack Details</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Remaining</TableCell>
                  {!isStudio && <TableCell sx={{ fontWeight: 600 }}>Pack Price</TableCell>}
                  <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Validity</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 80 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchasedPacks.map((pp, index) => {
                  const daysRemaining = getDaysRemaining(pp);
                  const usagePercent = getClassUsagePercent(pp);
                  const active = isPackActive(pp);
                  isDateExpired(pp);
                  const classesExhausted = isClassesExhausted(pp);

                  return (
                    <TableRow
                      key={pp._id}
                      hover={!!pp.user?._id}
                      onClick={() => pp.user?._id && handleOpenUserPanel(pp)}
                      sx={{
                        cursor: pp.user?._id ? 'pointer' : 'default',
                        ...(pp.user?._id ? { '&:hover': { backgroundColor: '#f5f5f5' } } : {}),
                        opacity: active ? 1 : 0.7,
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {(isSearching ? 0 : (page - 1) * pageSize) + index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Avatar sx={{ width: 36, height: 36, bgcolor: colors.primary }}>
                            {pp.user?.name?.charAt(0).toUpperCase() || 'U'}
                          </Avatar>
                          <Box>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                cursor: 'pointer',
                                '&:hover': { color: colors.primary, textDecoration: 'underline' },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                pp.user?._id && handleUserClick(pp.user._id);
                              }}
                            >
                              {pp.user?.name || 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {pp.user?.email || 'N/A'}
                            </Typography>
                            {pp.user?.phone && (
                              <Typography variant="caption" color="text.secondary">
                                {pp.user.phone}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {pp.title || pp.pack?.title || 'N/A'}
                          </Typography>
                          {pp.pack?.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                maxWidth: 200,
                              }}
                            >
                              {pp.pack.description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {isAllLocations && locationById(studioOf(pp)) ? (
                          <BranchChip location={locationById(studioOf(pp))} />
                        ) : pp.studio ? (
                          <Chip
                            label={pp.studio.studioName || 'Studio'}
                            size="small"
                            sx={{
                              backgroundColor: '#9C27B020',
                              color: '#9C27B0',
                              fontWeight: 500,
                            }}
                          />
                        ) : (
                          <Chip
                            label="SweatSocial"
                            size="small"
                            sx={{
                              backgroundColor: colors.primary + '20',
                              color: colors.primary,
                              fontWeight: 500,
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {pp.totalClasses ? (
                          <Box sx={{ minWidth: 100 }}>
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                              <Typography
                                variant="caption"
                                fontWeight={600}
                                color={remainingColor(pp) === '#F44336' ? '#F44336' : 'text.primary'}
                              >
                                {effectiveClassesLeft(pp)} left
                                {(pp.pendingBookingsCount ?? 0) > 0 && (
                                  <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                                    ({pp.pendingBookingsCount} upcoming)
                                  </Typography>
                                )}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                of {pp.totalClasses}
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={usagePercent || 0}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: '#e0e0e0',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 3,
                                  backgroundColor: remainingColor(pp),
                                },
                              }}
                            />
                            {/* Shared pack: one count across several categories / classes */}
                            {((pp.allowedCategories?.length ?? 0) > 0 || (pp.allowedClasses?.length ?? 0) > 0) && (
                              <Chip
                                size="small"
                                label={`Shared: ${
                                  ((pp.allowedCategories?.length ? pp.allowedCategories : pp.allowedClasses) ?? [])
                                    .map((x: any) => (typeof x === 'object' ? x?.title ?? x?.className : null))
                                    .filter(Boolean)
                                    .join(' · ') || 'several'
                                }`}
                                sx={{ height: 20, fontSize: 11, fontWeight: 600, mt: 0.75, backgroundColor: '#EEF2FF', color: '#3949AB', maxWidth: 260 }}
                              />
                            )}
                            {/* Combo pack: classes left per category / class */}
                            {(pp.categoryAllowances?.length ?? 0) > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                                {pp.categoryAllowances!.map((a, i) => {
                                  const name =
                                    a.appliesTo === 'wellness'
                                      ? 'Wellness'
                                      : a.appliesTo === 'class'
                                      ? (typeof a.class === 'object' ? a.class?.className : undefined) ?? 'Class'
                                      : (typeof a.category === 'object' ? a.category?.title : undefined) ?? 'Category';
                                  // remaining/total null = UNLIMITED bucket.
                                  const unlimited = a.remaining == null;
                                  const empty = !unlimited && (a.remaining ?? 0) <= 0;
                                  return (
                                    <Chip
                                      key={i}
                                      size="small"
                                      label={unlimited ? `${name} · ∞` : `${name} ${a.remaining}/${a.total}`}
                                      sx={{
                                        height: 20,
                                        fontSize: 11,
                                        fontWeight: 600,
                                        backgroundColor: empty ? '#FDECEA' : '#EEF2FF',
                                        color: empty ? '#C62828' : '#3730A3',
                                        '& .MuiChip-label': { px: 0.75 },
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Chip
                            label="Unlimited"
                            size="small"
                            sx={{ backgroundColor: '#4CAF5020', color: '#4CAF50', fontWeight: 500 }}
                          />
                        )}
                        {/* Expiring view: the member already bought their next pack. */}
                        {pp.renewedWith && (
                          <Tooltip
                            arrow
                            title={
                              <Box>
                                <Typography variant="caption" fontWeight={700} display="block">
                                  Already bought: {pp.renewedWith.title || 'another pack'}
                                </Typography>
                                {pp.renewedWith.purchaseDate && (
                                  <Typography variant="caption" display="block">
                                    Purchased {formatDate(pp.renewedWith.purchaseDate)}
                                  </Typography>
                                )}
                                {pp.renewedWith.startDate && new Date(pp.renewedWith.startDate) > new Date() && (
                                  <Typography variant="caption" display="block">
                                    Starts {formatDate(pp.renewedWith.startDate)}
                                  </Typography>
                                )}
                                <Typography variant="caption" display="block">
                                  {pp.renewedWith.classesLeft == null || pp.renewedWith.classesLeft < 0
                                    ? 'Unlimited sessions'
                                    : `${pp.renewedWith.classesLeft}${pp.renewedWith.totalClasses ? ` of ${pp.renewedWith.totalClasses}` : ''} sessions left`}
                                  {pp.renewedWith.endDate ? ` · valid till ${formatDate(pp.renewedWith.endDate)}` : ''}
                                </Typography>
                              </Box>
                            }
                          >
                            <Chip
                              label="Renewed"
                              size="small"
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                mt: 0.75,
                                height: 20,
                                fontSize: 11,
                                fontWeight: 700,
                                bgcolor: '#2E7D3218',
                                color: '#2E7D32',
                                border: '1px solid #2E7D3240',
                              }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      {!isStudio && (
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {pp.totalPrice ? formatCurrency(pp.totalPrice) : 'N/A'}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        {/* startDate is the optional deferred start (admin-set at
                            assignment); falls back to purchaseDate for packs that
                            started immediately, matching the backend's own
                            fallback (packs.service.ts ~line 5185). */}
                        <Typography variant="body2">{formatDate(pp.startDate || pp.purchaseDate)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {isUpcomingPack(pp) ? (
                            <>
                              <Typography variant="caption" color="text.secondary">Starts</Typography>
                              <Typography variant="body2" fontWeight={600} color="#7B1FA2">
                                {formatDate(pp.startDate!)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Expires {pp.expiryDate ? formatDate(pp.expiryDate) : pp.packEndDate ? formatDate(pp.packEndDate) : 'N/A'}
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Typography variant="body2">
                                {pp.expiryDate
                                  ? formatDate(pp.expiryDate)
                                  : pp.packEndDate
                                  ? formatDate(pp.packEndDate)
                                  : 'N/A'}
                              </Typography>
                              {daysRemaining !== null && active && (
                                <Typography
                                  variant="caption"
                                  color={daysRemaining < 7 ? 'error' : 'text.secondary'}
                                >
                                  {daysRemaining > 0 ? `${daysRemaining} days left` : 'Expires today'}
                                </Typography>
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-start">
                          {isUpcomingPack(pp) ? (
                            <Chip
                              label="Upcoming"
                              size="small"
                              sx={{ backgroundColor: '#7B1FA220', color: '#7B1FA2', fontWeight: 600 }}
                            />
                          ) : !classesExhausted && (
                            <Chip
                              label={active ? 'Active' : 'Expired'}
                              size="small"
                              sx={{
                                backgroundColor: active ? '#4CAF5020' : '#F4433620',
                                color: active ? '#4CAF50' : '#F44336',
                                fontWeight: 600,
                              }}
                            />
                          )}
                          {pp.isFrozen && (
                            <Chip
                              icon={<AcUnit sx={{ fontSize: '14px !important' }} />}
                              label="Frozen"
                              size="small"
                              sx={{
                                backgroundColor: '#2196F320',
                                color: '#2196F3',
                                fontWeight: 600,
                              }}
                            />
                          )}
                          {pp.isPaid === false && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Chip
                                label="Due"
                                size="small"
                                sx={{
                                  backgroundColor: colors.warning,
                                  color: colors.white,
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              />
                              {!isInstructor && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Payments sx={{ fontSize: 16 }} />}
                                  onClick={(e) => {
                                    e.stopPropagation(); // the row itself opens the member panel
                                    setMarkPaidTarget(pp);
                                  }}
                                  // Capsule, same height as the small status chips beside it.
                                  sx={{ textTransform: 'none', fontSize: '0.72rem', height: 24, py: 0, px: 1.25, minWidth: 0, whiteSpace: 'nowrap', borderRadius: 999 }}
                                >
                                  Mark paid
                                </Button>
                              )}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box display="flex" gap={0.5}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewDetails(pp)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="History">
                            <IconButton size="small" color="secondary" onClick={(e) => handleOpenHistory(pp, e)}>
                              <History fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Pack">
                            <IconButton size="small" color="primary" onClick={(e) => handleOpenEdit(pp, e)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {/* Studio packs only (a booking belongs to one studio), and
                              only where a super admin switched bulk booking on. */}
                          {isPackActive(pp) && !isInstructor && bulkBookingOn(pp) && (
                            <Tooltip title="Bulk book or cancel classes">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => { e.stopPropagation(); setBulkBookPack(pp); }}
                              >
                                <EventRepeat fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isPackActive(pp) && !isInstructor && (
                            pp.isFrozen ? (
                              <Tooltip title="Unfreeze Pack">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleUnfreeze(pp, e)}
                                  disabled={isUnfreezing === pp._id}
                                  sx={{ color: '#2196F3' }}
                                >
                                  {isUnfreezing === pp._id
                                    ? <CircularProgress size={14} />
                                    : <AcUnit fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Freeze Pack">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleOpenFreeze(pp, e)}
                                  sx={{ color: '#607D8B' }}
                                >
                                  <AcUnit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )
                          )}
                          {canDeletePurchasedPack && (
                            <Tooltip title="Delete Pack Subscription">
                              <IconButton size="small" color="error" onClick={(e) => handleDeletePackClick(pp, e)}>
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Pagination */}
        {!isSearching && totalPages > 1 && (
          <Box display="flex" justifyContent="center" py={2} borderTop="1px solid #e0e0e0">
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Card>

      {/* Mark as Paid confirmation — same layout as the Bookings page */}
      <Dialog open={!!markPaidTarget} onClose={() => !isMarkingPaid && setMarkPaidTarget(null)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ pt: 3 }}>
          {markPaidTarget && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>{markPaidTarget.user?.name || 'Member'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {markPaidTarget.title || markPaidTarget.pack?.title}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: 'action.hover',
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">Collected at studio</Typography>
                <Typography variant="body1" fontWeight={700}>
                  ₹{(markPaidTarget.amountPaid ?? markPaidTarget.totalPrice ?? 0).toLocaleString('en-IN')}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkPaidTarget(null)} disabled={isMarkingPaid}>
            Back
          </Button>
          <Button onClick={handleMarkPaidConfirm} variant="contained" color="primary" disabled={isMarkingPaid}>
            {isMarkingPaid ? 'Marking...' : 'Mark as Paid'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              Subscription Details
            </Typography>
            <IconButton size="small" onClick={() => setDetailDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPack && (
            <Box>
              {/* User Info */}
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Avatar sx={{ width: 56, height: 56, bgcolor: colors.primary, fontSize: 24 }}>
                  {selectedPack.user?.name?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {selectedPack.user?.name || 'Unknown User'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedPack.user?.email || 'N/A'}
                  </Typography>
                  {selectedPack.user?.phone && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedPack.user.phone}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Pack Info */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                PACK INFORMATION
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
                <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Inventory fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Pack Name
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedPack.title || selectedPack.pack?.title || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Store fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Type
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedPack.studio ? selectedPack.studio.studioName : 'SweatSocial (Global)'}
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <FitnessCenter fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Classes
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedPack.totalClasses
                      ? `${effectiveClassesLeft(selectedPack)} / ${selectedPack.totalClasses} remaining${(selectedPack.pendingBookingsCount ?? 0) > 0 ? ` (${selectedPack.pendingBookingsCount} upcoming)` : ''}`
                      : 'Unlimited'}
                  </Typography>
                </Box>
                {!isStudio && (
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <AttachMoney fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Price
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {selectedPack.totalPrice ? formatCurrency(selectedPack.totalPrice) : 'N/A'}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Dates */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                DATES & VALIDITY
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={2}>
                <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <CalendarToday fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Purchase Date
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDateTime(selectedPack.purchaseDate)}
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Timer fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Expiry Date
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedPack.expiryDate
                      ? formatDateTime(selectedPack.expiryDate)
                      : selectedPack.packEndDate
                      ? formatDateTime(selectedPack.packEndDate)
                      : 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 100%' }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <CheckCircle fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                    <Chip
                      label={isUpcomingPack(selectedPack) ? 'Upcoming' : isPackActive(selectedPack) ? 'Active' : 'Expired'}
                      color={isUpcomingPack(selectedPack) ? undefined : isPackActive(selectedPack) ? 'success' : 'error'}
                      sx={
                        isUpcomingPack(selectedPack)
                          ? { fontWeight: 600, backgroundColor: '#7B1FA220', color: '#7B1FA2' }
                          : { fontWeight: 600 }
                      }
                    />
                    {selectedPack.isFrozen && (
                      <Chip
                        icon={<AcUnit />}
                        label="Frozen"
                        sx={{ backgroundColor: '#2196F320', color: '#2196F3', fontWeight: 600 }}
                      />
                    )}
                  </Box>
                  {getDaysRemaining(selectedPack) !== null && isPackActive(selectedPack) && !isUpcomingPack(selectedPack) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {getDaysRemaining(selectedPack)} days remaining
                    </Typography>
                  )}
                  {selectedPack.isFrozen && selectedPack.frozenSince && (
                    <Typography variant="body2" sx={{ color: '#2196F3', mt: 1 }}>
                      Frozen since {formatDateTime(selectedPack.frozenSince)}
                      {selectedPack.scheduledUnfreezeDate && (
                        <> · unfreezes after {formatDate(selectedPack.scheduledUnfreezeDate)}</>
                      )}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Freeze Information — only for studio packs */}
              {renderFreezeSection(selectedPack)}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {selectedPack?.user?._id && (
            <Button variant="outlined" startIcon={<Person />} onClick={() => handleUserClick(selectedPack.user._id)}>
              View User Profile
            </Button>
          )}
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Edit />}
            onClick={() => {
              setDetailDialogOpen(false);
              handleOpenEdit(selectedPack!);
            }}
          >
            Edit Pack
          </Button>
          <Button variant="contained" onClick={() => setDetailDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Purchased Pack Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              Edit Pack
            </Typography>
            <IconButton size="small" onClick={() => setEditDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {editPack && (
            <Box display="flex" flexDirection="column" gap={2.5} pt={1}>
              <Typography variant="body2" color="text.secondary">
                <strong>{editPack.title || editPack.pack?.title}</strong> — {editPack.user?.name || 'Unknown'}
              </Typography>

              {isComboPack(editPack) && (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <Typography variant="body2" fontWeight={600}>
                    Classes remaining per category / class
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                    This is a combo pack — change one at a time. Reducing marks one class of that
                    category as used (a walk-in is recorded); adding tops that category up.
                  </Typography>
                  {(editPack.categoryAllowances ?? []).map((b) => {
                    const key = bucketKeyOf(b);
                    const name = bucketNameOf(b);
                    if (b.remaining == null) {
                      return (
                        <TextField key={key} label={name} size="small" value="Unlimited" disabled fullWidth />
                      );
                    }
                    const value = editBuckets[key] ?? String(b.remaining);
                    const added = Number(value) - (b.remaining ?? 0);
                    const otherChanged = Object.entries(editBuckets).some(
                      ([k, v]) => k !== key && v !== '' &&
                        Number(v) !== (editPack.categoryAllowances ?? []).find((x) => bucketKeyOf(x) === k)?.remaining,
                    );
                    return (
                      <TextField
                        key={key}
                        label={name}
                        type="number"
                        size="small"
                        value={value}
                        disabled={otherChanged}
                        onChange={(e) => setEditBuckets((prev) => ({ ...prev, [key]: e.target.value }))}
                        inputProps={{ min: Math.max(0, (b.remaining ?? 0) - 1) }}
                        helperText={
                          otherChanged
                            ? 'Save the other change first — one category at a time'
                            : `${b.remaining} of ${b.total ?? '—'} left${
                                Number.isFinite(added) && added > 0 ? ` · Adding ${added} → ${(b.total ?? 0) + added} total` : ''
                              } · Can reduce by 1 at a time`
                        }
                        fullWidth
                      />
                    );
                  })}
                </Box>
              )}

              {!isComboPack(editPack) && editPack.totalClasses && (
                <TextField
                  label="Classes Remaining"
                  type="number"
                  size="small"
                  value={editClassesLeft}
                  onChange={(e) => setEditClassesLeft(e.target.value)}
                  inputProps={{
                    // No upper cap: adding classes is a top-up — the server raises
                    // the pack's total by the same amount (3 of 3 → 5 of 5).
                    min: Math.max(0, (editPack.classesLeft ?? 0) - 1),
                  }}
                  helperText={(() => {
                    // Same split as the usage bar: "in process" = upcoming BOOKED
                    // sessions (credit already held), "used" = attended/missed.
                    const total = editPack.totalClasses ?? 0;
                    const inProcess = editPack.pendingBookingsCount ?? 0;
                    const used = Math.max(0, total - effectiveClassesLeft(editPack));
                    const added = Number(editClassesLeft) - (editPack.classesLeft ?? 0);
                    const isCombo = (editPack.categoryAllowances?.length ?? 0) > 0;
                    const scope = sharedScopeOf(editPack);
                    const topUp =
                      !isCombo && Number.isFinite(added) && added > 0
                        ? ` · Adding ${added} → total becomes ${total + added}`
                        : '';
                    return `Total ${total} classes, ${used} used, ${inProcess} in process${topUp} · Can reduce by 1 at a time${
                      scope ? ` · Usable on ${scope}` : ''
                    }`;
                  })()}
                  fullWidth
                />
              )}

              {/*
                Start Date — super admins and studio owners, on any pack, until
                the member books with it. Once booked it stays visible but
                locked: moving the start could leave an existing booking
                outside the pack's validity. The server enforces the same rule.
              */}
              {canManageStartDate && (() => {
                const locked = hasBookedSession(editPack);
                return (
                  <TextField
                    label="Start Date"
                    type="date"
                    size="small"
                    value={editStartDate}
                    disabled={locked}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEditStartDate(v);
                      // The expiry follows the start until it is edited by hand.
                      if (!editExpiryTouched && editPack.studio) {
                        const preview = expiryYmdFor(v, editPack.pack);
                        if (preview) setEditExpiryDate(preview);
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                    // No floor: a past date back-dates the pack. The server
                    // refuses one whose recalculated expiry is already past.
                    helperText={
                      locked
                        ? 'The member has already booked a session with this pack, so its start date can no longer be changed.'
                        : 'The pack is usable from 12:00 AM on this date. Changing it recalculates the expiry date.'
                    }
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarToday sx={{ fontSize: 16 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                );
              })()}

              <TextField
                label="Validity / Expiry Date"
                type="date"
                size="small"
                value={editExpiryDate}
                onChange={(e) => {
                  setEditExpiryDate(e.target.value);
                  setEditExpiryTouched(true);
                }}
                InputLabelProps={{ shrink: true }}
                helperText="The pack can be used through the end of this day (11:59 PM)."
                fullWidth
              />

              {isSuperAdmin && editPack.studio && (
                <TextField
                  label="Remaining Freeze Days (Override)"
                  type="number"
                  size="small"
                  value={editFreezeDaysOverride}
                  onChange={(e) => setEditFreezeDaysOverride(e.target.value)}
                  inputProps={{ min: 0 }}
                  helperText="Override the computed freeze budget. User will see exactly this many days remaining."
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AcUnit sx={{ fontSize: 16, color: '#455A64' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              {editError && (
                <Alert severity="error" onClose={() => setEditError(null)}>
                  {editError}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Class History
              </Typography>
              {historyPack && (
                <Typography variant="body2" color="text.secondary">
                  {historyPack.title || historyPack.pack?.title} — {historyPack.user?.name}
                </Typography>
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={() => setManualBookingOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Create Booking
              </Button>
              <IconButton size="small" onClick={() => setHistoryDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
              <CircularProgress />
            </Box>
          ) : historyError ? (
            <Box p={3}>
              <Alert severity="error">{historyError}</Alert>
            </Box>
          ) : historyItems.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={1}>
              <History sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography color="text.secondary">No sessions found for this pack</Typography>
            </Box>
          ) : (() => {
            const attended = historyItems.filter(i => i.bookingStatus === 'CHECKED_IN' || i.bookingStatus === 'NO_SHOW');
            // Everything not yet marked attended / no-show — upcoming or past.
            const booked = historyItems.filter(i => i.bookingStatus !== 'CHECKED_IN' && i.bookingStatus !== 'NO_SHOW');

            const colHead = (label: string) => <TableCell sx={{ fontWeight: 600, py: 1 }}>{label}</TableCell>;

            const historyRow = (item: any, index: number, greyed = false) => (
              <TableRow
                key={item.transactionId || index}
                sx={{ '&:last-child td': { border: 0 }, opacity: greyed ? 0.45 : 1 }}
              >
                <TableCell><Typography variant="body2" color="text.secondary">{index + 1}</Typography></TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={greyed ? 400 : 600}>{item.className}</Typography>
                  <Typography variant="caption" color="text.secondary" textTransform="capitalize">{item.bookingType || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{item.sessionDate ? formatDateTime(item.sessionDate) : '—'}</Typography>
                </TableCell>
                <TableCell>
                  {item.bookingStatus === 'CHECKED_IN' && item.statusChangedAt ? (
                    <Box sx={{ display: 'inline-flex', px: 1.5, py: 0.75, borderRadius: 2, backgroundColor: '#4CAF5015', border: '1px solid #4CAF5030' }}>
                      <Typography variant="body2" fontWeight={600} sx={{ color: '#2e7d32' }}>
                        {formatDateTime(item.statusChangedAt)}
                      </Typography>
                    </Box>
                  ) : item.bookingStatus === 'NO_SHOW' && item.statusChangedAt ? (
                    <Box sx={{ display: 'inline-flex', px: 1.5, py: 0.75, borderRadius: 2, backgroundColor: '#FF572215', border: '1px solid #FF572230' }}>
                      <Typography variant="body2" fontWeight={600} sx={{ color: '#e64a19' }}>
                        {formatDateTime(item.statusChangedAt)}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.disabled">—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {typeof item.bookingId === 'string' && item.bookingId.length <= 12
                      ? item.bookingId : String(item.bookingId).slice(-8)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5} alignItems="center" flexWrap="wrap">
                    <Chip
                      label={item.bookingStatus || '—'}
                      size="small"
                      sx={{
                        backgroundColor:
                          item.bookingStatus === 'CHECKED_IN' ? '#4CAF5020'
                          : item.bookingStatus === 'NO_SHOW' ? '#FF572220'
                          : item.bookingStatus === 'BOOKED' ? '#9E9E9E15'
                          : '#9E9E9E15',
                        color:
                          item.bookingStatus === 'CHECKED_IN' ? '#4CAF50'
                          : item.bookingStatus === 'NO_SHOW' ? '#FF5722'
                          : '#9E9E9E',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    />
                    {item.isDuetBooking && (
                      <Chip
                        label="+1"
                        size="small"
                        sx={{ backgroundColor: '#7c3aed20', color: '#7c3aed', fontWeight: 700, fontSize: '0.65rem', height: 20 }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ width: 48 }}>
                  {item.bookingMongoId && (
                    <Tooltip title="Update Status">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setHistoryBookingToUpdate({
                            _id: item.bookingMongoId.toString(),
                            bookingId: typeof item.bookingId === 'string' ? item.bookingId : String(item.bookingId),
                            bookingStatus: item.bookingStatus || 'BOOKED',
                            userName: historyPack?.user?.name || 'Unknown',
                          });
                          setHistoryNewStatus(item.bookingStatus || 'BOOKED');
                          setHistoryStatusReason('');
                          setHistoryCreditClassBack(true);
                          setHistoryStatusDialogOpen(true);
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );

            const tableHead = (
              <TableHead>
                <TableRow sx={{ backgroundColor: colors.primary + '10' }}>
                  {colHead('#')}
                  {colHead('Session / Class')}
                  {colHead('Session Date')}
                  {colHead('Attendance Time')}
                  {colHead('Booking ID')}
                  {colHead('Status')}
                  <TableCell sx={{ fontWeight: 600, py: 1, width: 48 }} />
                </TableRow>
              </TableHead>
            );

            return (
              <Box>
                {/* Checked in / No show first, then everything still BOOKED
                    (upcoming, or past and not marked yet) under its own small
                    heading — same order as the partner app's Class History. */}
                {attended.length > 0 && (
                  <>
                    <Box px={2} py={1} bgcolor="#f9fafb" borderBottom="1px solid #e5e7eb">
                      <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.8}>
                        Class Activity — {attended.length} session{attended.length !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        {tableHead}
                        <TableBody>
                          {attended.map((item, i) => historyRow(item, i, false))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

                {booked.length > 0 && (
                  <>
                    <Box
                      px={2}
                      py={1}
                      bgcolor="#f9fafb"
                      borderTop={attended.length > 0 ? '1px solid #e5e7eb' : undefined}
                      borderBottom="1px solid #e5e7eb"
                    >
                      <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.8}>
                        Booked — {booked.length} session{booked.length !== 1 ? 's' : ''} not attended yet
                      </Typography>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        {attended.length === 0 && tableHead}
                        <TableBody>
                          {booked.map((item, i) => historyRow(item, attended.length + i, false))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {!historyLoading && !historyError && (() => {
              const _now = new Date();
              const att = historyItems.filter(i => i.bookingStatus === 'CHECKED_IN' || i.bookingStatus === 'NO_SHOW').length;
              const upc = historyItems.filter(i =>
                (i.bookingStatus === 'BOOKED' || i.bookingStatus === 'IN_PROGRESS') &&
                i.sessionDate && new Date(i.sessionDate) >= _now
              ).length;
              if (att > 0 && upc > 0) return `${att} attended • ${upc} upcoming`;
              if (att > 0) return `${att} session${att !== 1 ? 's' : ''} attended`;
              if (upc > 0) return `${upc} upcoming session${upc !== 1 ? 's' : ''}`;
              return `${historyItems.length} session${historyItems.length !== 1 ? 's' : ''}`;
            })()}
          </Typography>
          <Button variant="contained" onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Booking Status Update Dialog (from pack history) */}
      <Dialog open={historyStatusDialogOpen} onClose={() => setHistoryStatusDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Update Booking Status</DialogTitle>
        <DialogContent>
          {historyBookingToUpdate && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Booking: <strong>{historyBookingToUpdate.bookingId}</strong>
                <br />User: <strong>{historyBookingToUpdate.userName}</strong>
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>New Status</InputLabel>
                <Select
                  value={historyNewStatus}
                  label="New Status"
                  onChange={(e) => {
                    const val = e.target.value;
                    setHistoryNewStatus(val);
                    if (val === 'CANCELLED') setHistoryCreditClassBack(true);
                    else if (val === 'BOOKED' || val === 'CHECKED_IN') setHistoryCreditClassBack(false);
                  }}
                >
                  <MenuItem value="BOOKED">Booked</MenuItem>
                  <MenuItem value="CHECKED_IN">Checked In</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  <MenuItem value="NO_SHOW">No Show</MenuItem>
                </Select>
              </FormControl>
              {historyNewStatus === 'CANCELLED' && historyBookingToUpdate.bookingStatus !== 'CANCELLED' && (
                <Box sx={{ border: '1px solid', borderColor: 'warning.main', borderRadius: 1, p: 1.5, bgcolor: 'warning.50' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={historyCreditClassBack}
                        onChange={(e) => setHistoryCreditClassBack(e.target.checked)}
                        color="warning"
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Credit 1 class back to user's pack</Typography>}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                    Check this if the booking used a pack class that should be returned (e.g. accidental check-in).
                  </Typography>
                </Box>
              )}
              {historyBookingToUpdate.bookingStatus === 'CANCELLED' && (historyNewStatus === 'BOOKED' || historyNewStatus === 'CHECKED_IN') && (
                <Box sx={{ border: '1px solid', borderColor: 'info.main', borderRadius: 1, p: 1.5, bgcolor: 'info.50' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={historyCreditClassBack}
                        onChange={(e) => setHistoryCreditClassBack(e.target.checked)}
                        color="info"
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Deduct 1 class from user's pack</Typography>}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                    Check this if the class was previously credited back when the booking was cancelled.
                  </Typography>
                </Box>
              )}
              <TextField
                label="Reason (optional)"
                value={historyStatusReason}
                onChange={(e) => setHistoryStatusReason(e.target.value)}
                size="small"
                fullWidth
                multiline
                rows={2}
                placeholder="Reason for status change..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryStatusDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleHistoryStatusUpdateConfirm}
            variant="contained"
            disabled={isUpdatingHistoryStatus || !historyNewStatus}
          >
            {isUpdatingHistoryStatus ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Purchased Pack Confirmation Dialog */}
      <Dialog open={deletePackDialogOpen} onClose={() => !isDeletingPack && setDeletePackDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>Delete Pack Subscription</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={1}>
            Are you sure you want to permanently delete this pack subscription?
          </Typography>
          {packToDelete && (
            <Box sx={{ bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5, mb: 1 }}>
              <Typography variant="body2" fontWeight={600}>{packToDelete.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                User: {packToDelete.user?.name} — Classes left: {effectiveClassesLeft(packToDelete)}
              </Typography>
            </Box>
          )}
          <Typography variant="caption" color="error.main">
            This action cannot be undone. The pack subscription will be permanently removed.
          </Typography>
          {deletePackError && <Alert severity="error" sx={{ mt: 1 }}>{deletePackError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeletePackDialogOpen(false)} disabled={isDeletingPack}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeletePackConfirm}
            disabled={isDeletingPack}
            startIcon={isDeletingPack ? <CircularProgress size={16} color="inherit" /> : <DeleteOutline />}
          >
            {isDeletingPack ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk pack booking */}
      <BulkPackBookingDialog
        open={!!bulkBookPack}
        onClose={() => setBulkBookPack(null)}
        onBooked={() => { fetchPurchasedPacks(); }}
        purchasedPackId={bulkBookPack?._id ?? null}
        studioId={bulkBookPack ? packStudioId(bulkBookPack) : undefined}
      />

      {/* Manual Booking Dialog */}
      <ManualBookingDialog
        open={manualBookingOpen}
        onClose={() => setManualBookingOpen(false)}
        onSuccess={() => { setManualBookingOpen(false); fetchPurchasedPacks(); refreshHistory(); }}
        studios={studios}
        prefilledUser={historyPack?.user ? {
          _id: historyPack.user._id,
          name: historyPack.user.name,
          email: historyPack.user.email,
          phoneNumber: historyPack.user.phone,
        } : undefined}
        prefilledStudio={
          historyPack?.studio?._id
            ? (studios.find((s) => s?._id === historyPack.studio!._id) ?? {
                _id: historyPack.studio._id,
                studioName: historyPack.studio.studioName || 'Studio',
              })
            : undefined
        }
        prefilledPackId={historyPack?._id}
        prefilledPackStartDate={historyPack?.startDate}
      />

      {/* Admin Freeze Pack Dialog */}
      <Dialog open={freezeDialogOpen} onClose={() => !isFreezing && setFreezeDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>Freeze Pack</Typography>
            <IconButton size="small" onClick={() => setFreezeDialogOpen(false)} disabled={isFreezing}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {freezeTargetPack && (
            <Box display="flex" flexDirection="column" gap={2.5} pt={1}>
              <Typography variant="body2" color="text.secondary">
                <strong>{freezeTargetPack.title}</strong> — {freezeTargetPack.user?.name}
              </Typography>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={freezeStartDate}
                onChange={(e) => {
                  const v = e.target.value;
                  setFreezeStartDate(v);
                  // Keep the resume date at least one day after the pause.
                  if (freezeEndDate && freezeEndDate <= v) setFreezeEndDate(nextDayOf(v));
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
                helperText={freezeStartDate === new Date().toISOString().split('T')[0] ? 'Today — pack freezes immediately' : 'Future date — pack freezes at 12am on the start date'}
                fullWidth
              />
              <TextField
                label="Resumes On"
                type="date"
                size="small"
                value={freezeEndDate}
                onChange={(e) => setFreezeEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                // A pack cannot come back on the day it pauses, so the earliest
                // resume date is always the next day.
                inputProps={{ min: nextDayOf(freezeStartDate) }}
                helperText={
                  freezeStartDate && freezeEndDate
                    ? `Frozen for ${frozenDayCount(freezeStartDate, freezeEndDate)} day${frozenDayCount(freezeStartDate, freezeEndDate) === 1 ? '' : 's'} — unfrozen at 12am on this date`
                    : 'The day the pack is unfrozen, at 12am'
                }
                fullWidth
              />
              <TextField
                label="Reason (optional)"
                size="small"
                value={freezeReason}
                onChange={(e) => setFreezeReason(e.target.value)}
                fullWidth
              />
              {freezeError && <Alert severity="error" onClose={() => setFreezeError(null)}>{freezeError}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setFreezeDialogOpen(false)} disabled={isFreezing}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmFreeze}
            disabled={isFreezing || !freezeStartDate || !freezeEndDate || freezeEndDate <= freezeStartDate}
            startIcon={isFreezing ? <CircularProgress size={16} color="inherit" /> : <AcUnit />}
          >
            {isFreezing ? 'Freezing…' : 'Freeze Pack'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Pack Assign Dialog */}
      <ManualPackAssignDialog
        open={assignPackOpen}
        onClose={() => setAssignPackOpen(false)}
        onSuccess={() => {
          setAssignPackOpen(false);
          fetchPurchasedPacks();
        }}
        studios={studios}
      />

      {/* Delete Freeze History Entry Dialog (super admin only) */}
      <Dialog
        open={deleteFreezeEntryDialogOpen}
        onClose={() => !isDeletingFreezeEntry && setDeleteFreezeEntryDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>Delete Freeze History Entry</Typography>
            <IconButton size="small" onClick={() => setDeleteFreezeEntryDialogOpen(false)} disabled={isDeletingFreezeEntry}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to permanently delete the freeze history entry for{' '}
            <strong>{freezeEntryToDelete?.month}</strong>? This action cannot be undone.
          </Typography>
          {deleteFreezeEntryError && <Alert severity="error" sx={{ mt: 1.5 }}>{deleteFreezeEntryError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteFreezeEntryDialogOpen(false)} disabled={isDeletingFreezeEntry}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteFreezeEntry}
            disabled={isDeletingFreezeEntry}
            startIcon={isDeletingFreezeEntry ? <CircularProgress size={16} color="inherit" /> : <DeleteOutline />}
          >
            {isDeletingFreezeEntry ? 'Deleting…' : 'Delete Entry'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User / Subscription Side Panel — opened by clicking a user in the table */}
      <Drawer
        anchor="right"
        open={userPanelOpen}
        onClose={() => setUserPanelOpen(false)}
        slotProps={{ paper: { sx: { width: { xs: '100%', sm: 600 }, maxWidth: '100%' } } }}
      >
        {selectedPack && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header — soft brand-tinted banner */}
            <Box
              sx={{
                p: 3,
                pb: 2,
                background: `linear-gradient(180deg, ${colors.primary}1f 0%, ${colors.background} 100%)`,
                borderBottom: `1px solid ${colors.divider}`,
              }}
            >
              <Box display="flex" justifyContent="flex-end" mb={-1}>
                <IconButton size="small" onClick={() => setUserPanelOpen(false)}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor: colors.primary,
                    fontSize: 24,
                    fontWeight: 700,
                    boxShadow: `0 4px 14px ${colors.primary}73`,
                  }}
                >
                  {selectedPack.user?.name?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
                <Box minWidth={0}>
                  <Typography variant="h6" fontWeight={700} noWrap>
                    {selectedPack.user?.name || 'Unknown User'}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.75} mt={0.5}>
                    <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {selectedPack.user?.email || 'N/A'}
                    </Typography>
                  </Box>
                  {selectedPack.user?.phone && (
                    <Box display="flex" alignItems="center" gap={0.75} mt={0.25}>
                      <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {selectedPack.user.phone}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              {selectedPack.user?._id && (
                <Button
                  size="small"
                  endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
                  onClick={() => handleUserClick(selectedPack.user._id)}
                  sx={{ mt: 1.5, ml: -1, textTransform: 'none', fontWeight: 700 }}
                >
                  View full profile
                </Button>
              )}
            </Box>

            {/* Scrollable body */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
              {/* At-a-glance — plain columns separated by hairlines, no boxes */}
              <Box sx={{ display: 'flex', mb: 4 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box mt={1}>
                    <Chip
                      size="small"
                      label={
                        <Box component="span" display="flex" alignItems="center" gap={0.5}>
                          {selectedPack.isFrozen && <AcUnit sx={{ fontSize: 13 }} />}
                          {selectedPack.isFrozen
                            ? 'Frozen'
                            : isUpcomingPack(selectedPack)
                            ? 'Upcoming'
                            : isClassesExhausted(selectedPack)
                            ? 'No Classes Left'
                            : isPackActive(selectedPack)
                            ? 'Active'
                            : 'Expired'}
                        </Box>
                      }
                      sx={{
                        px: 0.5,
                        ...(selectedPack.isFrozen
                          ? { backgroundColor: '#2196F320', color: '#2196F3', fontWeight: 700 }
                          : isUpcomingPack(selectedPack)
                          ? { backgroundColor: '#7B1FA220', color: '#7B1FA2', fontWeight: 700 }
                          : isClassesExhausted(selectedPack)
                          ? { backgroundColor: '#FF980020', color: '#FF9800', fontWeight: 700 }
                          : isPackActive(selectedPack)
                          ? { backgroundColor: '#4CAF5020', color: '#4CAF50', fontWeight: 700 }
                          : { backgroundColor: '#F4433620', color: '#F44336', fontWeight: 700 }),
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ flex: 1, pl: 2.5, borderLeft: `1px solid ${colors.divider}` }}>
                  <Typography variant="caption" color="text.secondary">Classes</Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
                    {selectedPack.totalClasses ? (
                      <>
                        {effectiveClassesLeft(selectedPack)}
                        <Typography component="span" variant="body2" color="text.secondary" fontWeight={500}>
                          {' '}/ {selectedPack.totalClasses}
                        </Typography>
                      </>
                    ) : (
                      'Unlimited'
                    )}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, pl: 2.5, borderLeft: `1px solid ${colors.divider}` }}>
                  <Typography variant="caption" color="text.secondary">
                    {isUpcomingPack(selectedPack) ? 'Starts In' : 'Expires In'}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
                    {(() => {
                      if (isUpcomingPack(selectedPack)) {
                        const days = Math.ceil((new Date(selectedPack.startDate!).getTime() - Date.now()) / 86400000);
                        return (
                          <>
                            {days}
                            <Typography component="span" variant="body2" color="text.secondary" fontWeight={500}> days</Typography>
                          </>
                        );
                      }
                      const days = getDaysRemaining(selectedPack);
                      if (days === null) return 'N/A';
                      if (days <= 0) return 'Today';
                      return (
                        <>
                          {days}
                          <Typography component="span" variant="body2" color="text.secondary" fontWeight={500}> days</Typography>
                        </>
                      );
                    })()}
                  </Typography>
                </Box>
              </Box>

              {/* Pack Information — light brand-tinted card */}
              {renderPanelSectionHeading('Pack Information')}
              <Box
                sx={{
                  backgroundColor: `${colors.primary}12`,
                  border: `1px solid ${colors.primary}30`,
                  borderRadius: 3,
                  p: 2,
                  mb: 4,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  rowGap: 2.5,
                  columnGap: 2,
                }}
              >
                {renderInfoField(Inventory, 'Pack Name', selectedPack.title || selectedPack.pack?.title || 'N/A', true)}
                {/* Studio admins only ever see their own studio's packs, so naming
                    the studio here would just repeat something already implied. */}
                {!isStudio &&
                  renderInfoField(
                    Store,
                    selectedPack.studio ? 'Studio' : 'Type',
                    selectedPack.studio ? selectedPack.studio.studioName : 'SweatSocial (Global)'
                  )}
                {renderInfoField(
                  Timer,
                  isUpcomingPack(selectedPack) ? 'Starts' : 'Expires',
                  isUpcomingPack(selectedPack)
                    ? formatDate(selectedPack.startDate!)
                    : selectedPack.expiryDate
                    ? formatDate(selectedPack.expiryDate)
                    : selectedPack.packEndDate
                    ? formatDate(selectedPack.packEndDate)
                    : 'N/A'
                )}
              </Box>

              {/* Payment — looked up from this user's transaction history (the pack
                  record itself has no link to its payment), with a receipt viewer
                  that reuses the exact Transactions-page view-receipt flow. Shown as
                  a plain divided strip (like the at-a-glance row above) rather than
                  another bordered card. */}
              {renderPanelSectionHeading('Payment')}
              <Box sx={{ mb: 4 }}>
                {packTransactionLoading ? (
                  <Box display="flex" justifyContent="center" py={1}>
                    <CircularProgress size={20} />
                  </Box>
                ) : packTransaction ? (
                  <>
                    <Box display="flex" alignItems="center">
                      {!isStudio && (
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">Amount Paid</Typography>
                          <Typography variant="body1" fontWeight={700} color="text.primary" sx={{ mt: 0.5 }}>
                            {formatCurrency(packTransaction.basePrice + packTransaction.taxes)}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ flex: 1, pl: isStudio ? 0 : 2.5, borderLeft: isStudio ? 'none' : `1px solid ${colors.divider}` }}>
                        <Typography variant="caption" color="text.secondary">Payment Mode</Typography>
                        <Typography
                          variant="body1"
                          fontWeight={700}
                          sx={{
                            mt: 0.5,
                            textTransform: 'capitalize',
                            // Same per-mode colour convention as the Transactions
                            // page for every mode, not just cash.
                            color: getPaymentModeColor(packTransaction.modeOfPayment),
                          }}
                        >
                          {packTransaction.modeOfPayment}
                        </Typography>
                      </Box>
                      {!isStudio && (
                        <Box sx={{ flex: 1, pl: 2.5, borderLeft: `1px solid ${colors.divider}` }}>
                          <Typography variant="caption" color="text.secondary">Source</Typography>
                          <Typography variant="body1" fontWeight={700} color="text.primary" sx={{ mt: 0.5 }}>
                            {sourceLabel(effectiveTransactionSource(packTransaction))}
                          </Typography>
                        </Box>
                      )}
                      <Button
                        size="small"
                        onClick={handleViewPackReceipt}
                        disabled={viewingPackReceipt}
                        startIcon={viewingPackReceipt ? <CircularProgress size={14} /> : undefined}
                        sx={{ color: colors.primary, ml: 1, fontSize: 12, textTransform: 'none', flexShrink: 0 }}
                      >
                        View Invoice
                      </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                      Purchased {formatDateTime(packTransaction.createdAt)}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.disabled">
                    No payment record found for this pack.
                  </Typography>
                )}
              </Box>

              {/* Freeze History — same underlying data as Subscription Details, shown as an activity log */}
              {selectedPack.studio && (
                <>
                  {renderPanelSectionHeading('Freeze History')}
                  {renderFreezeSection(selectedPack, 'log') || (
                    <Typography variant="body2" color="text.disabled">
                      No freezes recorded for this pack.
                    </Typography>
                  )}
                </>
              )}
            </Box>

            {/* Sticky actions — reuse the exact same Edit Pack / Class History dialogs used
                elsewhere on this page. Create Booking lives inside the History dialog's own
                corner, so one button opens that instead of duplicating a second entry point. */}
            <Box sx={{ p: 2.5, borderTop: `1px solid ${colors.divider}`, display: 'flex', gap: 1.5 }}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startIcon={<Edit sx={{ fontSize: 16 }} />}
                onClick={() => {
                  setUserPanelOpen(false);
                  handleOpenEdit(selectedPack!);
                }}
              >
                Edit Pack
              </Button>
              <Button
                fullWidth
                variant="contained"
                startIcon={<History sx={{ fontSize: 16 }} />}
                onClick={() => {
                  setUserPanelOpen(false);
                  handleOpenHistory(selectedPack!);
                }}
              >
                Create / View Booking
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default PurchasedPacksPage;
